import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings } from "lucide-react";
import styles from "./debugWidget.module.scss";

// === Navigation (сгруппировано) ===

interface DebugNavItem {
  path: string;
  label: string;
}

interface DebugNavGroup {
  title: string;
  items: readonly DebugNavItem[];
}

const DEBUG_NAV_GROUPS: readonly DebugNavGroup[] = [
  {
    title: "Старт",
    items: [
      { path: "/", label: "Splash" },
      { path: "/splash-glow-preview", label: "Splash glow" }
    ]
  },
  {
    title: "Инфо и вход",
    items: [
      { path: "/about", label: "About" },
      { path: "/login?step=legal", label: "Login: Legal" },
      { path: "/login?step=phone", label: "Login: Phone" },
      { path: "/login?step=otp", label: "Login: OTP" },
      { path: "/login?step=name", label: "Login: Name" }
    ]
  },
  {
    title: "Онбординг",
    items: [
      { path: "/onboarding?step=map", label: "Onboard: Map" },
      { path: "/onboarding?step=order", label: "Onboard: Order" },
      { path: "/onboarding?step=pickup", label: "Onboard: Получение" }
    ]
  },
  {
    title: "Основной поток",
    items: [
      { path: "/map", label: "Map" },
      { path: "/catalog", label: "Catalog" },
      { path: "/service", label: "Service" },
      { path: "/cart", label: "Cart" }
    ]
  },
  {
    title: "Фоны доменов",
    items: [
      { path: "/visual/print", label: "Print" },
      { path: "/visual/hub", label: "Hub" },
      { path: "/visual/link", label: "Link" },
      { path: "/visual/referral", label: "Referral" },
      { path: "/visual/sub", label: "Sub" }
    ]
  },
  {
    title: "Оплата и заказы",
    items: [
      { path: "/payment", label: "Payment" },
      { path: "/payment/status", label: "Payment Status" },
      { path: "/payment/settings", label: "Payment Settings" },
      { path: "/order/test", label: "Order" },
      { path: "/history", label: "History" }
    ]
  },
  {
    title: "Прочее",
    items: [
      { path: "/screen-backgrounds", label: "Галерея фонов" },
      { path: "/discounts", label: "Discounts" }
    ]
  }
] as const;

function isNavItemActive(
  pagePath: string,
  currentPath: string,
  pathname: string,
  search: string
): boolean {
  const pagePathname = pagePath.split("?")[0];
  const pageSearch = pagePath.includes("?") ? pagePath.split("?")[1] : null;
  return (
    currentPath === pagePath ||
    (pagePathname === pathname && !pageSearch && !search)
  );
}

// === Types ===

interface DebugOption {
  key: string;
  label: string;
}

// === Storage ===

const DEBUG_STORAGE_KEY = "debug_state";
const DEBUG_POSITION_KEY = "debug_widget_position";
const DEBUG_VISIBLE_KEY = "debug_widget_visible";
const TAPS_REQUIRED = 5;
const TAP_TIMEOUT = 500;

function getStoredStates(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(DEBUG_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function setStoredStates(states: Record<string, boolean>) {
  try {
    localStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(states));
  } catch {
    // Ignore storage errors
  }
  window.dispatchEvent(new Event("debug_state_changed"));
}

function getWidgetVisible(): boolean {
  try {
    return localStorage.getItem(DEBUG_VISIBLE_KEY) === "true";
  } catch {
    return false;
  }
}

function setWidgetVisible(visible: boolean) {
  try {
    localStorage.setItem(DEBUG_VISIBLE_KEY, String(visible));
  } catch {
    // Ignore
  }
}

function getStoredPosition(): { x: number; y: number } | null {
  try {
    const stored = localStorage.getItem(DEBUG_POSITION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function getCenterPosition() {
  return {
    x: Math.round(window.innerWidth / 2 - 20),
    y: Math.round(window.innerHeight / 2 - 20),
  };
}

// === Global Registry ===

const registeredOptions = new Map<string, DebugOption>();
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach(fn => fn());
}

function registerOption(option: DebugOption) {
  if (!registeredOptions.has(option.key)) {
    registeredOptions.set(option.key, option);
    notifyListeners();
  }
}

function unregisterOption(key: string) {
  if (registeredOptions.has(key)) {
    registeredOptions.delete(key);
    notifyListeners();
  }
}

function getRegisteredOptions(): DebugOption[] {
  return Array.from(registeredOptions.values());
}

// === Hooks ===

export function useDebugEmulations<T extends readonly DebugOption[]>(
  options: T
): { [K in T[number]["key"]]: boolean } {
  const [states, setStates] = useState(getStoredStates);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const opts = optionsRef.current;
    opts.forEach(opt => {
      registerOption(opt);
    });
    return () => {
      opts.forEach(opt => unregisterOption(opt.key));
    };
  }, [options]);

  useEffect(() => {
    const handler = () => setStates(getStoredStates());
    window.addEventListener("debug_state_changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("debug_state_changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  return useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const opt of optionsRef.current) {
      result[opt.key] = states[opt.key] ?? false;
    }
    return result as { [K in T[number]["key"]]: boolean };
  }, [states]);
}

// === Widget ===

export function DebugWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(getWidgetVisible);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState(getRegisteredOptions);
  const [position, setPosition] = useState(() => getStoredPosition() ?? getCenterPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [states, setStates] = useState(getStoredStates);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const tapCountRef = useRef(0);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Подписка на изменения регистрации опций
  useEffect(() => {
    const handler = () => setOptions(getRegisteredOptions());
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  // Подписка на изменения состояний
  useEffect(() => {
    const handler = () => setStates(getStoredStates());
    window.addEventListener("debug_state_changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("debug_state_changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  // Отслеживание 5 тапов для показа/скрытия виджета
  useEffect(() => {
    const handleTap = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(`.${styles.trigger}`) || target.closest(`.${styles.panel}`)) {
        return;
      }

      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }

      tapCountRef.current++;

      if (tapCountRef.current >= TAPS_REQUIRED) {
        const newVisible = !isVisible;
        setIsVisible(newVisible);
        setWidgetVisible(newVisible);
        if (newVisible) {
          // При появлении — по центру
          const center = getCenterPosition();
          setPosition(center);
          try {
            localStorage.setItem(DEBUG_POSITION_KEY, JSON.stringify(center));
          } catch {
            // Ignore
          }
        } else {
          setIsOpen(false);
        }
        tapCountRef.current = 0;
      } else {
        tapTimeoutRef.current = setTimeout(() => {
          tapCountRef.current = 0;
        }, TAP_TIMEOUT);
      }
    };

    document.addEventListener("click", handleTap);
    return () => {
      document.removeEventListener("click", handleTap);
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, [isVisible]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [position]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !dragRef.current) return;

    const deltaX = e.clientX - dragRef.current.startX;
    const deltaY = e.clientY - dragRef.current.startY;

    const newX = Math.max(0, Math.min(window.innerWidth - 48, dragRef.current.initialX + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - 48, dragRef.current.initialY + deltaY));

    setPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;

    const deltaX = Math.abs(e.clientX - dragRef.current.startX);
    const deltaY = Math.abs(e.clientY - dragRef.current.startY);

    if (deltaX < 5 && deltaY < 5) {
      setIsOpen(v => !v);
    }

    setIsDragging(false);
    try {
      localStorage.setItem(DEBUG_POSITION_KEY, JSON.stringify(position));
    } catch {
      // Ignore
    }
    dragRef.current = null;
  }, [position]);

  const toggleOption = (key: string) => {
    const newValue = !states[key];
    const newStates = { ...states, [key]: newValue };
    setStoredStates(newStates);
    setStates(newStates);
  };

  const handleNavigate = (path: string) => {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    navigate(normalizedPath);
    // Панель не закрываем — удобно смотреть Splash и переключать маршруты подряд; закрытие — снова по шестерёнке
  };

  // Только в dev режиме
  if (import.meta.env.PROD) return null;
  if (!isVisible) return null;

  return (
    <>
      <button
        className={styles.trigger}
        style={{ left: position.x, top: position.y }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <Settings size={24} />
      </button>

      {isOpen && (
        <div className={styles.panel} style={{ left: position.x + 50, top: position.y }}>
          <div className={styles.section}>
            <div className={styles.panelHeader}>Навигация</div>
            {DEBUG_NAV_GROUPS.map((group) => (
              <div key={group.title} className={styles.navGroup}>
                <div className={styles.navGroupTitle}>{group.title}</div>
                <div className={styles.navGrid}>
                  {group.items.map((page) => {
                    const currentPath = location.pathname + location.search;
                    const isActive = isNavItemActive(
                      page.path,
                      currentPath,
                      location.pathname,
                      location.search
                    );
                    return (
                      <button
                        key={page.path}
                        type="button"
                        className={`${styles.navButton} ${isActive ? styles.navButtonActive : ""}`}
                        onClick={() => handleNavigate(page.path)}
                      >
                        {page.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.section}>
            <div className={styles.panelHeader}>Эмуляции</div>
            {options.length === 0 ? (
              <div className={styles.empty}>Нет эмуляций для этой страницы</div>
            ) : (
              options.map(opt => (
                <label key={opt.key} className={styles.option}>
                  <input
                    type="checkbox"
                    checked={states[opt.key] ?? false}
                    onChange={() => toggleOption(opt.key)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))
            )}
          </div>

          <div className={styles.hint}>5 тапов по экрану — скрыть виджет</div>
        </div>
      )}
    </>
  );
}

// Dummy provider for backwards compatibility
export function DebugProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
